import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth';
import { API_BASE_URL } from '../config';

type QR = {
  id: string;
  target: string;
  imageUrl?: string;
  history?: any[];
};

export default function QRDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { jwt } = useAuth();
  const [qr, setQr] = useState<QR | null>(null);
  const [target, setTarget] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchQR() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/${id}`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });
        if (!res.ok) {
          throw new Error('Failed to fetch QR code');
        }
        const data = await res.json();
        setQr(data);
        setTarget(data.target);
      } catch (e) {
        if (e instanceof Error) {
          setError(e.message);
        } else {
          setError('Unknown error');
        }
      } finally {
        setLoading(false);
      }
    }
    fetchQR();
  }, [id, jwt]);

  async function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({ target }),
      });
      if (!res.ok) {
        throw new Error('Failed to update QR code');
      }
      navigate('/dashboard');
    } catch (e) {
      if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unknown error');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDownload() {
    // Download QR code image from backend (assume /r/{id} returns image)
    try {
      const url = `${API_BASE_URL.replace(/\/qr.*/, '')}/r/${id}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('Failed to fetch QR code image');
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `qr-${id}.png`;
      link.click();
    } catch (e: any) {
      alert(e.message || 'Download failed');
    }
  }

  if (loading) return <p className="text-gray-500">Loading...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!qr) return <p>Not found</p>;

  return (
    <div className="max-w-xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-4">QR Code: {id}</h1>
      <div className="flex flex-col items-center mb-6">
        <img
          src={qr.imageUrl || ''}
          alt="QR code"
          className="w-48 h-48 border rounded bg-white mb-2"
        />
        <button
          className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
          onClick={handleDownload}
        >
          Download
        </button>
      </div>
      <form onSubmit={handleSave} className="mb-6">
        <label className="block mb-2 font-medium">
          Target:
          <input
            className="border rounded px-2 py-1 w-full mt-1"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            required
          />
        </label>
        <button
          type="submit"
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 mt-2 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </form>
      <div>
        <h3 className="font-semibold mb-2">History</h3>
        <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">
          {JSON.stringify(qr.history || [], null, 2)}
        </pre>
      </div>
    </div>
  );
}
